import React, {useEffect, useState} from 'react';
import {Platform, View, Text} from 'react-native';
import {useStyles} from '../../../../shared/hooks/styles';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {LoadableContent} from '../../../../shared/components/loadableContent';
import {CustomScrollView} from '../../../../shared/components/scrollView';
import {Button} from '../../../../shared/components/button';
import {BaseFieldWrapper} from '../../../../shared/components/baseField';
import {AddressSelect} from '../../../../shared/components/address-select';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {useSelector} from 'react-redux';
import {WrappedFieldMetaProps} from 'redux-form';
import {useAppDispatch} from '../../../../redux';
import {esiaSendActivateCardAfterLogged} from '../../../../redux/actions/login';
import {refreshCardActivationDeliveryInfo} from '../../../../redux/actions/cardActivation';
import {ServerValidationError} from '../../../../shared/models/serverValidationError';
import {addNotification} from '../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../shared/components/snackbar/model';
import {esiaRegistrationInfoSelector} from '../../../../redux/selectors/login';
import {useTheme} from '../../../../shared/theme';
import {DeliveryType} from '../../../../types/registerCardApplicationRequestPayload';
import {CardApplicationStatus} from '../../../../types/enums/cardApplicationStatus';
import {QrCodeIcon} from '../../components/qrCodeIcon';
import {holderProfileSelector} from '../../../../redux/selectors/profile';
import {useAccountLinker} from '../../../../shared/hooks/useAccountLinkerHook/useAccountLinker';
import {useNavigation, useRoute} from '@react-navigation/native';
import {NavigationRouteProps} from '../../../../shared/routing/model';
import Screen from '../../../../shared/components/screen';
import {IssuePointSelectionMap} from '../components/issuePointSelectionMap/issuePointSelectionMap';
import {hasTrackingNumber, useDeliveryMethodChange} from '../useDeliveryMethodChange';
import {RussianPostDeliveryInfo} from '../components/russianPostDeliveryInfo';
import {IssuePointDeliveryInfo} from '../components/issuePointDeliveryInfo';
import {useMap} from '../../esiaRegCard/components/map/useMap';
import {getHolderProfile} from '../../../../redux/actions/profile';

export default ({
  onCloseModal,
  linkedAccountIdWeb,
  onIssuePointSelectionVisibleChange,
}: {
  onCloseModal?(): void;
  linkedAccountIdWeb?: number;
  onIssuePointSelectionVisibleChange?(visible: boolean): void;
}) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();

  const styles = useStyles(({mediaOnly, addShadow}) => {
    return {
      modalTitle: {
        paddingTop: 20,
        ...theme.fonts.h3,
      },
      modalWrap: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingHorizontal: 0,
        overflow: 'hidden',
        width: 878,
        margin: Platform.OS === 'ios' ? 1 : 0,
      },
      contentStyle: {
        paddingTop: 0,
      },
      backButton: {
        marginTop: 16,
      },
      kidsSectionTitle: {
        marginTop: 20,
        marginLeft: 20,
        ...theme.fonts.h3Light,
      },
      btn: {
        marginBottom: isDesktop ? 40 : 10,
        width: isDesktop ? '80%' : 260,
        textAlign: 'center',
        padding: 6,
        alignSelf: 'center',
        marginTop: 25,
      },
      header: {
        ...theme.topBar.header,
        elevation: 6,
        minHeight: 54,
        paddingRight: 10,
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
      },
      headerBackBtn: {
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
      },
      headerTitle: {
        paddingRight: 24,
      },
      formSubmitBox: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        marginBottom: 20,
        marginTop: 0,
        width: '100%',
        ...mediaOnly(DeviceSize.desktop, {
          alignItems: 'center',
          paddingHorizontal: 25,
          paddingBottom: 20,
        }),
      },
      form: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        ...mediaOnly(DeviceSize.desktop, {maxWidth: 878}),
      },
      table: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        backgroundColor: theme.color.backgroundMenuColor,
      },
      issuePointInfo: {
        minWidth: 70,
        ...mediaOnly(DeviceSize.desktop, {minHeight: 480}),
        marginRight: 20,
      },
      workingHrsInfo: {
        minWidth: 70,
        ...mediaOnly(DeviceSize.desktop, {minHeight: 480}),
        marginRight: 20,
      },
      workingHrsInfoTitle: {
        marginVertical: 20,
      },
      formSubmitBtnContainer: {
        paddingHorizontal: 20,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      },
      mapActions: {
        position: 'relative',
        zIndex: Platform.OS === 'android' ? 2 : undefined,
        elevation: Platform.OS === 'android' ? 2 : undefined,
        backgroundColor: Platform.OS === 'android' ? theme.color.backgroundMenuColor : undefined,
      },
      submit: {
        marginTop: 14,
        width: isDesktop ? '30%' : 260,
        zIndex: 3,
      },
      checkbox: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ...mediaOnly(DeviceSize.desktop, {width: '100%'}),
        ...mediaOnly(DeviceSize.tablet, {width: 560}),
        ...mediaOnly(DeviceSize.mobile, {width: 260}),
      },
      checkboxBtn: {
        marginRight: 12,
      },
      agreement: {
        ...mediaOnly(DeviceSize.desktop, {}, {flex: 1}),
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
      agreementText: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {...theme.fonts.bodySmallLight, lineHeight: 24}),
      },
      agreementTextDoc: {
        color: theme.color.primaryColor,
      },
      deliveryInfoContainer: {
        width: '100%',
        marginTop: isDesktop ? 0 : 24,
        paddingHorizontal: 14,
        position: 'relative',
      },
      subtitle: {
        textAlign: 'center',
        marginTop: 10,
        marginBottom: 10,
        position: 'relative',
        ...mediaOnly(DeviceSize.desktop, {
          paddingHorizontal: 20,
          width: '100%',
          alignSelf: 'stretch',
        }),
      },
      showTable: {
        transform: [{translateY: 0}],
      },
      hideTable: {
        transform: [{translateY: -250}],
      },
      mapBlock: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: 24}),
        width: '100%',
        borderRadius: 5,
        ...addShadow({height: 2, radius: 18, opacity: 0.1}),
      },
      mapWrapper: {
        height: 480,
      },
      row: {
        marginBottom: 20,
      },
      rowWithIcon: {
        flexDirection: 'row',
      },
      icon: {
        marginRight: 12,
        color: theme.color.primaryColor,
        justifyContent: 'center',
        height: 30,
      },
      map: {
        width: '100%',
        height: '100%',
      },
      groupItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 12,
        paddingVertical: 5,
      },
      cluster: {
        color: theme.color.primaryColor,
      },
      clusterText: {
        color: theme.color.labelText,
      },
      issuePointInfoContainer: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
        paddingHorizontal: 16,
      },
      issuePointInfoText: {
        flexShrink: 1,
      },
      trackingNumberLink: {
        color: theme.color.linkColor,
      },
    };
  });

  const dispatch = useAppDispatch();

  const {linkedAccounts, loadLinkedAccountsInfo} = useAccountLinker();
  const navigation = useNavigation();

  const route: NavigationRouteProps<{'linkedAccount-id'?: number}> = useRoute();
  const linkedAccountIdMobile = route?.params?.['linkedAccount-id'];
  const esiaRegInfoFromSelector = useSelector(esiaRegistrationInfoSelector);
  const profile = useSelector(holderProfileSelector());
  let linkedAccountIdToParse: number | null = null;
  if (linkedAccountIdWeb) {
    linkedAccountIdToParse = linkedAccountIdWeb;
  } else if (linkedAccountIdMobile) {
    linkedAccountIdToParse = +linkedAccountIdMobile;
  }
  const isOwnAccount = linkedAccountIdToParse == null;

  const esiaRegInfoFromLinkedAccount =
    linkedAccountIdToParse != null ? linkedAccounts.find((acc) => acc.id === linkedAccountIdToParse) : undefined;

  const esiaRegInfo =
    [esiaRegInfoFromLinkedAccount, esiaRegInfoFromSelector].find(
      (info) => info?.cardDeliveryInfo?.deliveryTypeId != null,
    ) ??
    esiaRegInfoFromLinkedAccount ??
    esiaRegInfoFromSelector;

  React.useEffect(() => {
    void dispatch(refreshCardActivationDeliveryInfo());
  }, [dispatch]);

  useEffect(() => {
    if (!isOwnAccount) {
      return;
    }

    dispatch(getHolderProfile());
  }, [dispatch, isOwnAccount]);

  const [loading, setLoading] = useState(false);
  const [cardNumberToActivate, setCardNumberToActivate] = useState<string>('');

  const onActivateCardClick = async (event?: any) => {
    event?.preventDefault();
    const normalizedCardNumber = (cardNumberToActivate ?? '').trim();
    if (!normalizedCardNumber) {
      dispatch(
        addNotification({
          message: 'Введите номер карты для активации',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }
    if (profile) {
      try {
        setLoading(true);
        await dispatch(
          esiaSendActivateCardAfterLogged({
            CardNumber: normalizedCardNumber,
            CounterpartyLinkId: linkedAccountIdMobile,
          }),
        );
        await loadLinkedAccountsInfo();
        if (isOwnAccount) {
          await dispatch(getHolderProfile());
        }
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
        dispatch(
          addNotification({
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      } finally {
        setLoading(false);
        navigation.goBack();
        return;
      }
    }
  };

  function onOrderFormClose(): void {
    onCloseModal?.();
  }

  const activationIsUnavailable = React.useMemo(() => {
    const hasApprovedPrerequestedApplication = isOwnAccount
      ? profile?.prerequestedCardApplicationId != null
      : esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.Accepted;
    const hasCreatedPrerequestedApplication =
      esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.AcceptFromClient;

    const deliveryTypeId = isOwnAccount
      ? profile?.prerequestedCardApplicationDeliveryTypeId ?? esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId
      : esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId;

    return (
      (hasApprovedPrerequestedApplication || hasCreatedPrerequestedApplication) &&
      deliveryTypeId === DeliveryType.RussianPost &&
      !hasTrackingNumber(isOwnAccount ? profile : null, esiaRegInfo)
    );
  }, [isOwnAccount, profile, esiaRegInfo]);

  const issuePointsMap = useMap({showOnlyAvailableCards: !activationIsUnavailable});

  const deliveryMethodChange = useDeliveryMethodChange({
    activationIsUnavailable,
    isOwnAccount: linkedAccountIdToParse == null,
    cardApplicationId: profile?.prerequestedCardApplicationId,
    profile,
    esiaRegInfo,
    issuePointsMap,
  });

  useEffect(() => {
    onIssuePointSelectionVisibleChange?.(deliveryMethodChange.isIssuePointSelectionVisible);

    if (!onIssuePointSelectionVisibleChange && !isDesktop) {
      navigation.setOptions({
        title: deliveryMethodChange.isIssuePointSelectionVisible ? 'Выбрать пункт выдачи' : 'Активировать карту',
      });
    }
  }, [deliveryMethodChange.isIssuePointSelectionVisible, isDesktop, navigation, onIssuePointSelectionVisibleChange]);

  return (
    <Screen>
      <LoadableContent loading={loading}>
        {!loading ? (
          <CustomScrollView
            contentContainerStyle={styles.formSubmitBox}
            scrollEnabled={false}
            withRefresh={false}
            contentInsetAdjustmentBehavior='automatic'>
            <View style={styles.form}>
              {!deliveryMethodChange.isIssuePointSelectionVisible &&
                !deliveryMethodChange.isRussianPostAddressInputVisible && (
                  <>
                    {esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId === DeliveryType.RussianPost ? (
                      <RussianPostDeliveryInfo
                        esiaRegInfo={esiaRegInfo}
                        cardStatusFallback='New'
                        contentAlign={isDesktop ? 'center' : 'justify'}
                        canChangeDeliveryMethod={deliveryMethodChange.canChangeDeliveryMethod}
                      />
                    ) : (
                      <IssuePointDeliveryInfo
                        esiaRegInfo={esiaRegInfo}
                        linkedAccountIdWeb={linkedAccountIdWeb}
                        viewVariantForWeb='afterLoggedOnMobile'
                        issuePointsMap={issuePointsMap}
                        contentAlign={isDesktop ? 'center' : 'justify'}
                        canChangeDeliveryMethod={deliveryMethodChange.canChangeDeliveryMethod}
                      />
                    )}

                    <View testID='styles.formSubmitBtnContainer' style={styles.formSubmitBtnContainer}>
                      {!activationIsUnavailable && (
                        <>
                          <BaseFieldWrapper
                            disabled={false}
                            onCustomBlur={() => {}}
                            input={{
                              value: cardNumberToActivate,
                              onChange: (e) => setCardNumberToActivate(e ?? ''),
                            }}
                            label={
                              Platform.OS !== 'web' ? 'Номер карты для активации' : 'Укажите номер карты для активации'
                            }
                            type='digits'
                            autoCapitalize='none'
                            name='cardNumToActivate'
                            readOnly={false}
                            inputContainerStyle={Platform.OS !== 'web' ? {width: '85%'} : undefined}
                            style={
                              isDesktop
                                ? {width: '80%', marginBottom: 10, marginTop: 20, maxWidth: '345px', zIndex: 3}
                                : {width: '100%', marginBottom: 0, marginTop: 20, zIndex: 3}
                            }
                            right={
                              Platform.OS !== 'web' ? (
                                <QrCodeIcon onScan={setCardNumberToActivate} adjustFor='input' />
                              ) : undefined
                            }
                          />

                          <Button
                            style={styles.submit}
                            disabled={loading}
                            onPress={onActivateCardClick}
                            text='Активировать карту'
                            id='esia-active-card-form-submit'
                          />
                        </>
                      )}

                      {deliveryMethodChange.canChangeDeliveryMethod && (
                        <Button
                          style={[styles.submit, {height: 'auto', minHeight: 40}]}
                          disabled={loading}
                          onPress={deliveryMethodChange.openDeliveryMethodChange}
                          text='Изменить способ получения'
                          id='esia-select-issuepoint-btn'
                        />
                      )}

                      {Platform.OS === 'web' &&
                        isDesktop &&
                        !profile &&
                        !deliveryMethodChange.isIssuePointSelectionVisible && (
                          <Button
                            style={styles.submit}
                            disabled={loading}
                            onPress={onOrderFormClose}
                            text='Выход'
                            id='esia-form-close'
                          />
                        )}
                    </View>
                  </>
                )}

              {deliveryMethodChange.isRussianPostAddressInputVisible && (
                <View style={{width: '100%'}}>
                  <Text style={styles.subtitle}>
                    Вы меняете способ получения карты на Почту России. Укажите адрес, на который нужно доставить карту.
                  </Text>
                  <View style={{paddingHorizontal: isDesktop ? 20 : 0}}>
                    <AddressSelect
                      value={deliveryMethodChange.russianPostDeliveryAddress}
                      onChange={deliveryMethodChange.setRussianPostDeliveryAddress}
                      setAddress={deliveryMethodChange.setRussianPostDeliveryAddress}
                      label='Адрес получателя'
                      name='russianPostDeliveryAddress'
                      baseFieldProps={{
                        inputContainerStyle: Platform.OS !== 'web' ? {width: '85%'} : undefined,
                        style: {width: '100%', marginBottom: isDesktop ? 10 : 0, marginTop: 20, zIndex: 3},
                        autoFocus: true,
                        meta: {
                          error: {error: 'Адрес должен относиться к Свердловской области'},
                          touched: !deliveryMethodChange.isRussianPostDeliveryAddressValid,
                        } as WrappedFieldMetaProps,
                      }}
                    />
                  </View>

                  <View testID='styles.formSubmitBtnContainer' style={styles.formSubmitBtnContainer}>
                    <Button
                      style={styles.submit}
                      disabled={
                        loading ||
                        deliveryMethodChange.deliveryChangeSubmitting ||
                        !deliveryMethodChange.russianPostDeliveryAddressText ||
                        !deliveryMethodChange.isRussianPostDeliveryAddressValid
                      }
                      onPress={deliveryMethodChange.onConfirmRussianPost}
                      text='Сохранить изменения'
                      id='russianpost-delivery-change-submit'
                    />

                    <Button
                      style={styles.submit}
                      disabled={loading || deliveryMethodChange.deliveryChangeSubmitting}
                      onPress={deliveryMethodChange.closeDeliveryMethodChange}
                      text='Отмена'
                      id='russianpost-delivery-change-back'
                    />
                  </View>
                </View>
              )}

              {deliveryMethodChange.isIssuePointSelectionVisible && (
                <View style={{width: '100%'}}>
                  <IssuePointSelectionMap
                    mapData={deliveryMethodChange.mapData}
                    mapLoading={deliveryMethodChange.mapLoading}
                    currentMarker={deliveryMethodChange.currentMarker}
                    mapCenterPoint={deliveryMethodChange.mapCenterPoint}
                    mapZoomLevel={deliveryMethodChange.mapZoomLevel}
                    nativeRegion={deliveryMethodChange.nativeRegion}
                    onIssuePointSelected={deliveryMethodChange.onIssuePointSelected}
                    onNativeIssuePointSelected={deliveryMethodChange.onNativeIssuePointSelected}
                    onPressMarker={deliveryMethodChange.onPressMarker}
                    isIssuePointDetailsVisible={deliveryMethodChange.isIssuePointDetailsVisible}
                    setIsIssuePointDetailsVisible={deliveryMethodChange.setIsIssuePointDetailsVisible}
                    lastMarkerClickTimestamp={deliveryMethodChange.lastMarkerClickTimestamp}
                    setLastMarkerClickTimestamp={deliveryMethodChange.setLastMarkerClickTimestamp}
                    destroyNativeMap={deliveryMethodChange.destroyNativeMap}
                  />

                  <View
                    testID='styles.formSubmitBtnContainer'
                    style={[styles.formSubmitBtnContainer, styles.mapActions]}>
                    <Button
                      style={styles.submit}
                      disabled={
                        loading || deliveryMethodChange.deliveryChangeSubmitting || !deliveryMethodChange.currentMarker
                      }
                      onPress={deliveryMethodChange.onConfirmIssuePoint}
                      text='Сохранить изменения'
                      id='issuepoint-delivery-change-submit'
                    />

                    <Button
                      style={styles.submit}
                      disabled={loading || deliveryMethodChange.deliveryChangeSubmitting}
                      onPress={deliveryMethodChange.closeDeliveryMethodChange}
                      text='Отмена'
                      id='issuepoint-delivery-change-back'
                    />
                  </View>
                </View>
              )}
            </View>
          </CustomScrollView>
        ) : null}
      </LoadableContent>
    </Screen>
  );
};
